import { GridSize } from '@mui/material';
import { DefaultTextProps } from '@web-for-marketing/react-ui';
import { LanguageCode } from '@models/languageCode';

interface BaseType {
    id: string;
    required?: boolean;
    labelKey?: string;
    placeholderKey?: string;
    hidden?: boolean;
    width?: GridSize;
    startAdornmentText?: {
        contentKey: string;
        props?: DefaultTextProps;
        fontSize?: string;
    }[];
}

interface TextType extends BaseType {
    name: 'first_name' | 'last_name' | 'company' | 'city' | 'email' | 'phone' | 'AB_name__c' | 'AB_treatment__c';
    type: 'text' | 'email' | 'tel';
    initialValue: string;
}

interface SelectType extends BaseType {
    name: 'Title__c' | 'industry' | 'fleet_size' | 'country' | 'How_Did_You_Hear_About_Geotab__c';
    type: 'select';
    initialValue: string;
    optionsArray?: string[];
    optionsComponentKey?: string;
}

interface ConditionalInputType extends BaseType {
    name: 'user_title' | 'Title';
    type: 'conditional-input';
    initialValue: string;
}

interface ConditionalSelectType extends BaseType {
    name: 'Sub_Industry__c' | 'state';
    type: 'conditional-select';
    initialValue: string;
}

interface CheckboxType extends BaseType {
    name: 'opt_in';
    type: 'checkbox';
    initialValue: boolean;
}

interface RadioType extends BaseType {
    name: 'opt_in';
    type: 'radio';
    initialValue: boolean;
    radioOptions: Array<{ content: string; value: boolean }>;
}

interface MultiSelectCheckboxType extends BaseType {
    name: 'multiple_region_select__c';
    type: 'multi-select-checkbox';
    optionsArray: string[];
    initialValue: string[];
    preSubmitTransform?: string[];
    helperTextKey?: string;
    checkBoxWidth?: GridSize;
    mobileCheckBoxWidth?: GridSize;
    forceMultiPick?: boolean;
    optionalText?: string;
}

export type SiteWideDemoFormField =
    | TextType
    | SelectType
    | ConditionalInputType
    | ConditionalSelectType
    | CheckboxType
    | RadioType
    | MultiSelectCheckboxType;

export interface SiteWideDemoFormStructureSection {
    sectionDescription: string;
    formFields: SiteWideDemoFormField[];
    button: {
        text: string;
        type: 'button' | 'reset' | 'submit' | undefined;
    };
}

export interface SiteWideDemoFormStructure {
    tabs: SiteWideDemoFormStructureSection[];
}

export const getSiteWideDemoFormFields = (
    siteWideDemoFormStructure: SiteWideDemoFormStructure
): SiteWideDemoFormField[] =>
    siteWideDemoFormStructure.tabs.reduce<SiteWideDemoFormField[]>(
        (acc, formGroup) => acc.concat(formGroup.formFields),
        []
    );

type ExtractInitialValue<T> = T extends { initialValue: infer R } ? R : never;

type InferTextTypeInitialValue = ExtractInitialValue<TextType>;
type InferSelectTypeInitialValue = ExtractInitialValue<SelectType>;
type InferConditionalInputTypeInitialValue = ExtractInitialValue<ConditionalInputType>;
type InferConditionalSelectTypeInitialValue = ExtractInitialValue<ConditionalSelectType>;
type InferCheckboxTypeInitialValue = ExtractInitialValue<CheckboxType>;
type InferMultiCheckboxTypeInitialValue = ExtractInitialValue<MultiSelectCheckboxType>;

export type SiteWideDemoFormFieldValues = {
    first_name: InferTextTypeInitialValue;
    last_name: InferTextTypeInitialValue;
    company: InferTextTypeInitialValue;
    city: InferTextTypeInitialValue;
    email: InferTextTypeInitialValue;
    phone: InferTextTypeInitialValue;
    AB_name__c: InferTextTypeInitialValue;
    AB_treatment__c: InferTextTypeInitialValue;
    How_Did_You_Hear_About_Geotab__c: InferSelectTypeInitialValue;
    Title__c: InferSelectTypeInitialValue;
    industry: InferSelectTypeInitialValue;
    fleet_size: InferSelectTypeInitialValue;
    country: InferSelectTypeInitialValue;
    user_title: InferConditionalInputTypeInitialValue;
    Title: InferConditionalInputTypeInitialValue;
    Sub_Industry__c: InferConditionalSelectTypeInitialValue;
    state: InferConditionalSelectTypeInitialValue;
    opt_in: InferCheckboxTypeInitialValue;
    multiple_region_select__c: InferMultiCheckboxTypeInitialValue;
    page: number;
    Lead_Source_URL__c: string;
    utm_campaign__c?: string;
    utm_content__c?: string;
    utm_medium__c?: string;
    utm_source__c?: string;
    utm_term__c?: string;
    GCLID__c?: string;
    recaptchaToken?: string;
    campaign_id?: string;
    Lead_Source_Additional_Details__c?: string;
    LeadSource?: string;
    Form_Language_Country__c?: string;
    assetName?: string;
    assetType?: string;
    assetUrl?: string;
    reseller__c?: string;
    database__c?: string;
};

/* eslint-disable camelcase */
export const initialEmptyObject: SiteWideDemoFormFieldValues = {
    first_name: '',
    last_name: '',
    Title__c: '',
    Title: '',
    AB_name__c: '',
    AB_treatment__c: '',
    company: '',
    industry: '',
    Sub_Industry__c: '',
    multiple_region_select__c: [],
    fleet_size: '',
    country: '',
    state: '',
    city: '',
    email: '',
    phone: '',
    How_Did_You_Hear_About_Geotab__c: '',
    opt_in: false,
    user_title: '',
    page: 0,
    Lead_Source_URL__c: '',
    assetName: '',
    assetType: '',
    assetUrl: '',
};

interface PreSubmitTransform {
    fieldKey: string;
    transforms: string[];
}

export const getAllPreSubmitTransforms = (siteWideDemoFormFields: SiteWideDemoFormField[]): PreSubmitTransform[] =>
    siteWideDemoFormFields.reduce(
        (acc: PreSubmitTransform[], fieldObject) =>
            fieldObject.type === 'multi-select-checkbox' && fieldObject.preSubmitTransform && fieldObject.name
                ? (acc.push({
                    fieldKey: fieldObject.name,
                    transforms: fieldObject.preSubmitTransform,
                }),
                acc)
                : acc,
        []
    );

export const getAllRequiredFields = (siteWideDemoFormFields: SiteWideDemoFormField[]): string[] =>
    siteWideDemoFormFields.reduce((acc: string[], fieldObject) => {
        if (fieldObject.required && fieldObject.name) {
            acc.push(fieldObject.name);
        }
        return acc;
    }, []);

export const getCurrentDisabledButtons = (
    currentFormValues: SiteWideDemoFormFieldValues,
    baseStructureData: SiteWideDemoFormStructureSection[]
): boolean[] => {
    const disabledButtons: boolean[] = [];

    baseStructureData.forEach((fieldGroup) => {
        const fields = fieldGroup.formFields;
        const shouldDisable = fields.some((field) => {
            if (field.required && !currentFormValues[field.name]) {
                return true;
            }
        });

        disabledButtons.push(shouldDisable);
    });

    return disabledButtons;
};

export const translatedSiteWideDemoFormStructure: Record<LanguageCode, string> = {
    [LanguageCode.en]: 'en',
    [LanguageCode.enGb]: 'en-gb',
    [LanguageCode.frCa]: 'fr-ca',
    [LanguageCode.es]: 'es',
    [LanguageCode.it]: 'it',
    [LanguageCode.frFr]: 'fr',
    [LanguageCode.ptBr]: 'pt-br',
    [LanguageCode.de]: 'de',
    [LanguageCode.enAu]: 'en-au',
    [LanguageCode.esMx]: 'es-mx',
    [LanguageCode.pl]: 'pl',
    [LanguageCode.nl]: 'nl',
    [LanguageCode.se]: 'en',
    [LanguageCode.ae]: 'en',
    [LanguageCode.ie]: 'en-ie',
    [LanguageCode.apac]: 'en-sg',
    [LanguageCode.enCb]: 'en',
    [LanguageCode.idn]: 'en',
    [LanguageCode.jp]: 'en',
    [LanguageCode.pt]: 'pt',
};
